<template>
    <v-container class="mt-10 pb-12"
        style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <h1 class="text-h4 font-weight-bold mb-2">Meetings</h1>
                    <p class="text-subtitle-1 grey--text mb-0">Schedule and manage team meetings</p>
                </div>
                <div class="d-flex" style="gap: 8px;">
                    <v-btn color="primary" outlined :loading="refreshing" @click="refreshData" class="text-none">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>

                    <!-- Google Calendar Button -->
                    <v-btn
                        v-if="calendarConnected"
                        color="success"
                        outlined
                        class="text-none"
                        @click="openCalendarDialog"
                    >
                        <v-icon left>mdi-google</v-icon>
                        Calendar Connected
                        <v-icon right small>mdi-calendar-check</v-icon>
                    </v-btn>
                    <v-btn
                        v-else
                        color="grey darken-1"
                        outlined
                        class="text-none"
                        :loading="calendarConnecting"
                        @click="connectGoogleCalendar"
                    >
                        <v-icon left>mdi-google</v-icon>
                        Connect Google Calendar
                    </v-btn>

                    <v-btn color="orange darken-2" dark elevation="2" @click="quick_meeting_dialog = true" class="text-none font-weight-bold">
                        <v-icon left>mdi-lightning-bolt</v-icon>
                        Quick Meeting
                    </v-btn>
                    <v-btn color="primary" @click="openCreateMeetingDialog" class="text-none">
                        <v-icon left>mdi-plus</v-icon>
                        Create Meeting
                    </v-btn>
                </div>
            </div>


            <!-- Filters Row -->
            <div class="filters-row d-flex align-center mb-4" style="gap: 16px;">
                <!-- Search Bar -->
                <v-text-field v-model="searchQuery" placeholder="Search meetings..." prepend-inner-icon="mdi-magnify"
                    outlined dense hide-details clearable class="search-input" style="min-width: 140px;" />
                <!-- Frequency Filter -->
                <v-select v-model="selectedFrequency" :items="frequencyOptions" label="All Types" outlined dense
                    hide-details class="filter-select" style="min-width: 140px;" menu-props="offsetY">
                    <template v-slot:selection="{ item }">
                        <span>{{ item.text }}</span>
                    </template>
                    <template v-slot:item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                        </v-list-item-content>
                    </template>
                </v-select>

                <!-- Timing Filter -->
                <v-select v-model="selectedTiming" :items="timingOptions" outlined dense hide-details
                    class="filter-select" style="min-width: 140px;" menu-props="offsetY">
                    <template v-slot:selection="{ item }">
                        <span>{{ item.text }}</span>
                    </template>
                    <template v-slot:item="{ item }">
                        <v-list-item-content>
                            <v-list-item-title
                                :class="{ 'primary--text font-weight-bold': item.value === selectedTiming }">
                                {{ item.text }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </template>
                </v-select>

                <!-- Company Filter -->
                <v-select v-model="selectedCompanyId" :items="companyFilterOptions" item-text="name" item-value="id"
                    label="All Companies" outlined dense hide-details class="filter-select" style="min-width: 180px;"
                    menu-props="offsetY">
                    <template v-slot:selection="{ item }">
                        <span>{{ item.name || 'All Companies' }}</span>
                    </template>
                </v-select>
            </div>
        </div>

        <!-- Meetings List -->
        <div class="meetings-list">
            <!-- Loading State -->
            <div v-if="loading">
                <v-skeleton-loader type="card" class="mb-4" />
                <v-skeleton-loader type="card" class="mb-4" />
                <v-skeleton-loader type="card" class="mb-4" />
            </div>

            <div v-else-if="visibleMeetings.length > 0" class="meetings-container">
                <!-- Recurring Meetings Section -->
                <div v-if="recurringMeetings.length > 0" class="meeting-section mb-6">
                    <div class="section-header d-flex align-center mb-3">
                        <v-icon color="success" class="mr-2">mdi-repeat</v-icon>
                        <h2 class="text-h6 font-weight-bold mb-0">Recurring Meetings</h2>
                        <v-chip small class="ml-2" color="success" text-color="white">
                            {{ recurringMeetings.length }}
                        </v-chip>
                    </div>
                    <div class="meetings-scroll-container">
                        <v-card v-for="meeting in recurringMeetings" :key="meeting.id" class="meeting-item mb-3"
                            @click="openMeeting(meeting)" hover outlined>
                            <v-card-text class="pa-4">
                                <div class="d-flex align-center justify-space-between">
                                    <!-- Left side: Meeting info -->
                                    <div class="meeting-info flex-grow-1">
                                        <div class="d-flex align-center mb-2">
                                            <h3 class="text-h6 font-weight-medium mr-3">{{ meeting.name }}</h3>
                                            <v-chip small :color="getMeetingFrequencyColor(meeting.frequency)"
                                                class="mr-2">
                                                {{ meeting.frequency || 'once' }}
                                            </v-chip>
                                        </div>

                                        <div class="d-flex align-center text-body-2 grey--text">
                                            <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
                                            <span class="mr-4">{{ formatMeetingDate(meeting.created_at) }}</span>

                                            <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                                            <span class="mr-4">{{ formatMeetingTime(meeting.created_at) }}</span>

                                            <v-icon small color="grey" class="mr-1">mdi-office-building</v-icon>
                                            <span>{{ meeting.company?.name || 'No Company' }}</span>
                                        </div>

                                        <!-- Recurrence Pattern Info for recurring meetings -->
                                        <div class="recurrence-info mt-2">
                                            <div class="d-flex align-center text-caption grey--text">
                                                <v-icon x-small color="success" class="mr-1">mdi-repeat</v-icon>
                                                <span>
                                                    Every {{ meeting.recurrence_interval || 1 }} 
                                                    {{ (meeting.recurrence_interval || 1) === 1 ? 'week' : 'weeks' }}
                                                    <span v-if="meeting.recurrence_days && meeting.recurrence_days.length > 0">
                                                        on {{ formatRecurrenceDays(meeting.recurrence_days) }}
                                                    </span>
                                                </span>
                                            </div>
                                            <div v-if="meeting.series_start_date" class="d-flex align-center text-caption grey--text mt-1">
                                                <v-icon x-small color="grey" class="mr-1">mdi-calendar-start</v-icon>
                                                <span>Series: {{ formatMeetingDate(meeting.series_start_date) }}</span>
                                                <span v-if="meeting.series_end_date"> - {{ formatMeetingDate(meeting.series_end_date) }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Right side: Creator and actions -->
                                    <div class="meeting-actions d-flex align-center">
                                        <div class="creator-info text-right mr-3">
                                            <div class="text-caption grey--text">Created by:</div>
                                            <div class="d-flex align-center justify-end">
                                                <v-avatar size="24" :color="getAvatarColor(getCreatorName(meeting))"
                                                    class="mr-2">
                                                    <span class="white--text text-caption">
                                                        {{ getInitials(getCreatorName(meeting)) }}
                                                    </span>
                                                </v-avatar>
                                                <span class="text-body-2 font-weight-medium">
                                                    {{ getCreatorName(meeting) }}
                                                </span>
                                            </div>
                                        </div>

                                        <v-menu bottom left>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item @click="editMeetingInline(meeting)">
                                                    <v-list-item-icon>
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>Edit</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                                <v-list-item @click="openDeleteMeetingDialog(meeting)">
                                                    <v-list-item-icon>
                                                        <v-icon small color="error">mdi-delete</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>Delete</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                </div>

                <!-- One-Time Meetings Section -->
                <div v-if="onceMeetings.length > 0" class="meeting-section">
                    <div class="section-header d-flex align-center mb-3">
                        <v-icon color="primary" class="mr-2">mdi-calendar-check</v-icon>
                        <h2 class="text-h6 font-weight-bold mb-0">One-Time Meetings</h2>
                        <v-chip small class="ml-2" color="primary" text-color="white">
                            {{ onceMeetings.length }}
                        </v-chip>
                    </div>
                    <div class="meetings-scroll-container">
                        <v-card v-for="meeting in onceMeetings" :key="meeting.id" class="meeting-item mb-3"
                            @click="openMeeting(meeting)" hover outlined>
                            <v-card-text class="pa-4">
                                <div class="d-flex align-center justify-space-between">
                                    <!-- Left side: Meeting info -->
                                    <div class="meeting-info flex-grow-1">
                                        <div class="d-flex align-center mb-2">
                                            <h3 class="text-h6 font-weight-medium mr-3">{{ meeting.name }}</h3>
                                            <v-chip small :color="getMeetingFrequencyColor(meeting.frequency)"
                                                class="mr-2">
                                                {{ meeting.frequency || 'once' }}
                                            </v-chip>
                                        </div>

                                        <div class="d-flex align-center text-body-2 grey--text">
                                            <v-icon small color="grey" class="mr-1">mdi-calendar</v-icon>
                                            <span class="mr-4">{{ formatMeetingDate(meeting.created_at) }}</span>

                                            <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                                            <span class="mr-4">{{ formatMeetingTime(meeting.created_at) }}</span>

                                            <v-icon small color="grey" class="mr-1">mdi-office-building</v-icon>
                                            <span>{{ meeting.company?.name || 'No Company' }}</span>
                                        </div>
                                    </div>

                                    <!-- Right side: Creator and actions -->
                                    <div class="meeting-actions d-flex align-center">
                                        <div class="creator-info text-right mr-3">
                                            <div class="text-caption grey--text">Created by:</div>
                                            <div class="d-flex align-center justify-end">
                                                <v-avatar size="24" :color="getAvatarColor(getCreatorName(meeting))"
                                                    class="mr-2">
                                                    <span class="white--text text-caption">
                                                        {{ getInitials(getCreatorName(meeting)) }}
                                                    </span>
                                                </v-avatar>
                                                <span class="text-body-2 font-weight-medium">
                                                    {{ getCreatorName(meeting) }}
                                                </span>
                                            </div>
                                        </div>

                                        <v-menu bottom left>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                                                    <v-icon>mdi-dots-vertical</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense>
                                                <v-list-item @click="editMeetingInline(meeting)">
                                                    <v-list-item-icon>
                                                        <v-icon small>mdi-pencil</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>Edit</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                                <v-list-item @click="openDeleteMeetingDialog(meeting)">
                                                    <v-list-item-icon>
                                                        <v-icon small color="error">mdi-delete</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-content>
                                                        <v-list-item-title>Delete</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state text-center py-8">
                <v-icon size="64" color="grey lighten-1">mdi-calendar-blank</v-icon>
                <h3 class="text-h5 grey--text mt-4 mb-2">No meetings found</h3>
                <p class="text-body-1 grey--text mb-4">
                    Try adjusting your filters or create a new meeting.
                </p>
                <v-btn @click="openCreateMeetingDialog()" color="primary" large>
                    <v-icon left>mdi-plus</v-icon>
                    Create Meeting
                </v-btn>
            </div>
        </div>

        <!-- Create Meeting Dialog -->
        <CreateMeetingDialog v-model="create_meeting_dialog" :users="users()" :companies="companies()"
            :calendar-connected="calendarConnected"
            @create-meeting="handleCreateMeeting" />

        <!-- Quick Create Meeting Dialog -->
        <QuickCreateMeetingDialog v-model="quick_meeting_dialog" :users="users()" :companies="companies()"
            @create-meeting="handleCreateMeeting" />

        <!-- Google Calendar Dialog -->
        <v-dialog v-model="calendar_dialog" max-width="640" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                        <v-icon color="success" class="mr-2">mdi-google</v-icon>
                        Google Calendar
                        <v-chip small color="success" text-color="white" class="ml-2">Connected</v-chip>
                    </div>
                    <v-btn icon @click="calendar_dialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>

                <v-divider />

                <!-- Calendar User Info -->
                <v-card-text v-if="calendarUser" class="pt-4 pb-2">
                    <div class="d-flex align-center mb-4">
                        <v-icon color="grey" class="mr-2">mdi-account-circle</v-icon>
                        <div>
                            <div class="text-body-2 font-weight-medium">{{ calendarUser.email || 'Google Account' }}</div>
                            <div class="text-caption grey--text">
                                <span v-if="calendarSource === 'google_events'">Connected for calendar event syncing</span>
                                <span v-else>Synced via Google Calendar (recording)</span>
                            </div>
                        </div>
                        <v-spacer />
                        <v-btn
                            v-if="calendarSource === 'recall'"
                            small text color="grey"
                            :loading="calendarRefreshing"
                            @click="refreshCalendarMeetings"
                        >
                            <v-icon left small>mdi-refresh</v-icon> Refresh
                        </v-btn>
                    </div>

                    <!-- Google Events info banner -->
                    <v-alert v-if="calendarSource === 'google_events'" type="info" dense text class="mb-3">
                        Your Google Calendar is connected for syncing meetings. To enable meeting recording, use <strong>Connect Google Calendar</strong> again to link the recording bot.
                    </v-alert>

                    <!-- Auto-record toggle (Recall.ai only) -->
                    <template v-if="calendarSource === 'recall'">
                        <v-switch
                            v-model="autoRecordAll"
                            :loading="savingPreferences"
                            color="success"
                            dense
                            class="mt-0 mb-4"
                            @change="saveRecordingPreferences"
                        >
                            <template v-slot:label>
                                <span class="text-body-2">Auto-record all calendar meetings</span>
                            </template>
                        </v-switch>

                        <v-divider class="mb-4" />

                        <!-- Upcoming Calendar Meetings -->
                        <div class="d-flex align-center mb-3">
                            <h3 class="text-subtitle-1 font-weight-bold mb-0">Upcoming Meetings</h3>
                            <v-chip x-small class="ml-2" color="primary" text-color="white">{{ calendarMeetings.length }}</v-chip>
                        </div>

                        <div v-if="calendarMeetingsLoading" class="text-center py-4">
                            <v-progress-circular indeterminate color="primary" size="28" />
                        </div>

                        <div v-else-if="calendarMeetings.length === 0" class="text-center py-4 grey--text">
                            <v-icon size="36" color="grey lighten-1">mdi-calendar-blank</v-icon>
                            <div class="text-body-2 mt-2">No upcoming calendar meetings found</div>
                        </div>

                        <v-list v-else dense class="pa-0">
                            <v-list-item
                                v-for="cm in calendarMeetings"
                                :key="cm.id"
                                class="calendar-meeting-item px-0"
                            >
                                <v-list-item-content>
                                    <v-list-item-title class="text-body-2 font-weight-medium">{{ cm.raw && cm.raw.summary || 'Untitled' }}</v-list-item-title>
                                    <v-list-item-subtitle class="text-caption">
                                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                                        {{ formatCalendarDate(cm.raw && (cm.raw.start && cm.raw.start.dateTime || cm.raw.start && cm.raw.start.date)) }}
                                        <span v-if="cm.raw && cm.raw.conferenceData" class="ml-2">
                                            <v-icon x-small color="success" class="mr-1">mdi-video</v-icon>Has video link
                                        </span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-switch
                                        :input-value="cm.override_should_record !== false"
                                        dense color="success"
                                        :loading="cm._saving"
                                        title="Override auto-record for this meeting"
                                        @change="toggleMeetingRecord(cm, $event)"
                                    />
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </template>
                </v-card-text>

                <v-card-text v-else-if="calendarLoadError" class="text-center py-6">
                    <v-icon size="48" color="warning">mdi-calendar-alert</v-icon>
                    <div class="text-body-2 mt-3 grey--text">Could not load calendar details.</div>
                    <v-btn small text color="primary" class="mt-3" @click="openCalendarDialog">
                        <v-icon left small>mdi-refresh</v-icon> Retry
                    </v-btn>
                </v-card-text>

                <v-card-text v-else class="text-center py-6">
                    <v-progress-circular indeterminate color="primary" />
                </v-card-text>

                <v-divider />
                <v-card-actions>
                    <v-btn text color="error" :loading="calendarDisconnecting" @click="disconnectGoogleCalendar">
                        <v-icon left small>mdi-link-off</v-icon>
                        Disconnect Calendar
                    </v-btn>
                    <v-spacer />
                    <v-btn text @click="calendar_dialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Include the component -->
        <!-- Note: Make sure to register the component in the components section -->

        <!-- Delete Meeting Dialog -->
        <v-dialog v-model="delete_meeting_dialog" persistent max-width="400">
            <v-card>
                <v-card-title class="text-h6">Delete Meeting</v-card-title>
                <v-card-text>
                    <p class="mb-2">Are you sure you want to delete this meeting?</p>
                    <p class="text-subtitle-2 font-weight-medium">{{ meeting_to_delete?.name }}</p>
                    <p class="text-caption grey--text">This action cannot be undone.</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeDeleteMeetingDialog()" text>Cancel</v-btn>
                    <v-btn @click="deleteMeeting()" color="error" :loading="deleting">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import CreateMeetingDialog from "./dialogs/CreateMeetingDialog.vue";
import QuickCreateMeetingDialog from "./dialogs/QuickCreateMeetingDialog.vue";

export default {
    inject: ['users', 'meetings', 'companies', 'refreshMeetings', 'refreshCompanies', 'refreshUsers'],
    name: "MeetingsComponent",
    components: {
        CreateMeetingDialog,
        QuickCreateMeetingDialog,
    },
    data: () => ({
        creating: false,
        deleting: false,
        loading: false,
        refreshing: false,
        app_url: null,
        api_url: null,
        searchQuery: '',
        create_meeting_dialog: false,
        quick_meeting_dialog: false,
        selectedCompanyId: null,
        selectedFrequency: 'all',
        selectedTiming: 'all',
        delete_meeting_dialog: false,
        meeting_to_delete: null,
        // Google Calendar
        calendarConnected: false,
        calendarConnecting: false,
        calendarUser: null,
        calendarMeetings: [],
        calendarMeetingsLoading: false,
        calendarRefreshing: false,
        calendarDisconnecting: false,
        savingPreferences: false,
        autoRecordAll: false,
        calendar_dialog: false,
        calendarExpiredHandled: false,
        calendarJustConnectedAt: 0,
        calendarAuthRecoveryInProgress: false,
        calendarLoadError: false,
        calendarSource: null, // 'recall' | 'google_events' | null
    }),
    created() {
        this.app_url = process.env.VUE_APP_URL;
        this.api_url = process.env.VUE_APP_API_URL;
    },
    async mounted() {
        document.title = "DG Tasks - Meetings";
        this.initializeComponent();
        // Restore persisted connected state immediately so button doesn't flicker on refresh
        if (localStorage.getItem('calendar_connected') === 'true') {
            this.calendarConnected = true;
        }
        const handledOAuth = await this.handleCalendarOAuthReturn();
        if (!handledOAuth) {
            this.checkCalendarStatus({ suppressAuthExpiredToast: true, noCache: true });
        }
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_meeting_statuses: "meetings/get_meeting_statuses",
        }),

        // Check if user can create meetings (admins only)
        canCreateMeetings() {
            return this.get_user.role_id <= 2 || this.get_user.role_id == 11; // Admin and Manager roles
        },

        // Filter options for frequency
        frequencyOptions() {
            return [
                { text: 'All Types', value: 'all' },
                { text: 'Once', value: 'once' },
                { text: 'Recurring', value: 'recurring' }
            ];
        },

        // Filter options for timing
        timingOptions() {
            return [
                { text: 'All Meetings', value: 'all' },
                { text: 'Upcoming', value: 'upcoming' },
                { text: 'Past', value: 'past' }
            ];
        },

        // Filter meetings based on user access, search, and filters
        visibleMeetings() {
            const allMeetings = this.meetings() || [];
            if (!allMeetings.length) return [];

            let filteredMeetings = allMeetings.filter(meeting => {
                // Access control filter
                if (this.get_user.role_id < 3 || this.get_user.role_id == 11) {
                    // Admins and managers can see all meetings
                } else {
                    // Regular users can see meetings they created OR have access to
                    const isCreator = this.get_user.id == meeting.user_id;
                    const hasAccess = meeting.access && meeting.access.some(access => access.user_id === this.get_user.id);

                    if (!isCreator && !hasAccess) {
                        return false;
                    }
                }

                // Search filter
                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase();
                    if (!meeting.name.toLowerCase().includes(query) &&
                        !(meeting.agenda && meeting.agenda.toLowerCase().includes(query))) {
                        return false;
                    }
                }

                // Company filter
                if (this.selectedCompanyId) {
                    if (meeting.company_id !== this.selectedCompanyId) {
                        return false;
                    }
                }

                // Frequency filter
                if (this.selectedFrequency !== 'all') {
                    const meetingFreq = meeting.frequency || 'once';
                    if (this.selectedFrequency !== meetingFreq) {
                        return false;
                    }
                }

                // Timing filter
                if (this.selectedTiming !== 'all') {
                    const now = new Date();
                    const meetingDate = this.getMeetingStart(meeting);

                    if (this.selectedTiming === 'upcoming' && meetingDate <= now) {
                        return false;
                    }
                    if (this.selectedTiming === 'past' && meetingDate >= now) {
                        return false;
                    }
                }

                return true;
            });

            return filteredMeetings.sort((a, b) => this.getMeetingStart(b) - this.getMeetingStart(a));
        },

        // Company filter options
        companyFilterOptions() {
            const allOption = { id: null, name: 'All Companies' };
            const allCompanies = this.companies() || [];
            return [allOption, ...allCompanies];
        },

        // Separate recurring meetings
        recurringMeetings() {
            return this.visibleMeetings.filter(meeting =>
                meeting.frequency === 'recurring'
            );
        },

        // Separate one-time meetings
        onceMeetings() {
            return this.visibleMeetings.filter(meeting =>
                !meeting.frequency || meeting.frequency === 'once'
            );
        },
    },
    methods: {
        // Actual scheduled start of a meeting (meeting_date + start_time), falling back to created_at
        getMeetingStart(meeting) {
            if (meeting.meeting_date) {
                const datePart = String(meeting.meeting_date).substring(0, 10);
                const timePart = meeting.start_time ? String(meeting.start_time).substring(0, 8) : '00:00:00';
                const start = new Date(`${datePart}T${timePart.length === 5 ? timePart + ':00' : timePart}`);
                if (!isNaN(start)) return start;
            }
            return new Date(meeting.created_at);
        },

        async initializeComponent() {
            // Data is already loaded via injection from DefaultLayout
            // Only show loading if we don't have data yet
            const hasMeetings = this.meetings() && this.meetings().length > 0;

            if (!hasMeetings) {
                this.loading = true;
            }

            try {
                await Promise.all([
                    this.fetchMeetings(),
                    this.fetchCompanies()
                ]);
            } catch (error) {
                console.error('Error initializing meetings component:', error);
                this.$toast.error('Failed to load meetings data');
            } finally {
                this.loading = false;
            }
        },

        async fetchCompanies() {
            try {
                await this.refreshCompanies();
            } catch (error) {
                console.error('Error fetching companies:', error);
                this.$toast.error('Failed to load companies');
            }
        },

        async fetchMeetings() {
            try {
                await this.refreshMeetings();
            } catch (error) {
                console.error('Error fetching meetings:', error);
                this.$toast.error('Failed to load meetings');
            }
        },

        openMeeting(item) {
            this.$router.push({ name: "ViewMeeting", params: { id: item.id } });
        },

        openCreateMeetingDialog() {
            this.create_meeting_dialog = true;
        },

        closeCreateMeetingDialog() {
            this.create_meeting_dialog = false;
        },

        async handleCreateMeeting(payload) {
            this.creating = true;
            try {
                // Validate conference link is provided and is a valid URL
                if (!payload.conference_link || !payload.conference_link.trim()) {
                    this.$toast.error('Conference link is required to create a meeting');
                    this.creating = false;
                    return;
                }
                const link = payload.conference_link.trim();
                const isValidUrl = /^(https?:\/\/)[^\s]+$/i.test(link);
                const containsMeetLink = /https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}/i.test(link);
                const containsHttpsUrl = /https?:\/\/(?!tel\.meet)[^\s]+/i.test(link);
                if (!isValidUrl && !containsMeetLink && !containsHttpsUrl) {
                    this.$toast.error('Conference link must be a valid URL (e.g. https://meet.google.com/...)');
                    this.creating = false;
                    return;
                }

                const syncToCalendar = !!payload.sync_to_calendar;
                delete payload.sync_to_calendar;

                const response = await this.$axios.post("api/meetings/create_meeting", payload);

                if (response.data.response) {
                    this.$toast.success(response.data.message);
                    
                    // Get the created meeting ID from response
                    const createdMeeting = response.data.data;
                    
                    // Automatically start recording bot if meeting has conference link
                    if (createdMeeting && createdMeeting.id && payload.conference_link) {
                        await this.startRecordingBot(createdMeeting.id);
                    }

                    // Sync to Google Calendar if requested
                    if (syncToCalendar && createdMeeting && createdMeeting.id) {
                        await this.syncMeetingToGoogleCalendar(createdMeeting.id, payload);
                    }
                    
                    await this.fetchMeetings();
                } else {
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error creating meeting:', error);
                this.$toast.error('Failed to create meeting');
            } finally {
                this.creating = false;
            }
        },

        async syncMeetingToGoogleCalendar(meetingId, meetingPayload) {
            try {
                // Build full ISO datetimes from separate date + time fields
                // meeting_date: "2026-03-28", start_time: "09:00" → "2026-03-28T09:00:00"
                const date = meetingPayload?.meeting_date;
                const startTime = meetingPayload?.start_time;
                const endTime = meetingPayload?.end_time;

                const startDatetime = date && startTime ? `${date}T${startTime}:00` : null;
                const endDatetime = date && endTime ? `${date}T${endTime}:00` : null;

                const body = {
                    title: meetingPayload?.name,
                    description: meetingPayload?.agenda || '',
                    start_datetime: startDatetime,
                    end_datetime: endDatetime,
                    conference_link: meetingPayload?.conference_link || null,
                    attendees: meetingPayload?.attendees || [],
                };

                const response = await this.$axios.post(`api/meetings/${meetingId}/sync-calendar`, body);
                if (response.data.response) {
                    this.$toast.success('Meeting added to Google Calendar');
                } else {
                    console.warn('Calendar sync warning:', response.data.message);
                }
            } catch (error) {
                console.warn('Could not sync to Google Calendar:', error);
            }
        },

        async startRecordingBot(meetingId) {
            try {
                const response = await this.$axios.post(`api/meetings/${meetingId}/start-recording`, {
                    bot_name: 'DG VENTURES Note taker'
                });
                
                if (response.data.response) {
                    this.$toast.success('Recording bot started successfully');
                } else {
                    // Don't show error to user as meeting was created successfully
                    // Just log it for debugging
                    console.warn('Failed to start recording bot:', response.data.message);
                }
            } catch (error) {
                // Don't show error to user as meeting was created successfully
                console.error('Error starting recording bot:', error);
            }
        },

        editMeetingInline(meeting) {
            // For now, redirect to meeting view for editing
            // Could implement inline editing dialog here
            this.openMeeting(meeting);
        },

        async updateMeeting(item) {
            try {
                const payload = {
                    name: item.name,
                    agenda: item.agenda,
                    meeting_status_id: item.meeting_status_id,
                    duration: item.duration,
                };

                const response = await this.$axios.post(`api/meetings/update_meeting/${item.id}`, payload);

                if (response.data.response) {
                    this.$toast.success(response.data.message);
                    await this.fetchMeetings();
                } else {
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error updating meeting:', error);
                this.$toast.error('Failed to update meeting');
            }
        },

        openDeleteMeetingDialog(item) {
            this.delete_meeting_dialog = true;
            this.meeting_to_delete = item;
        },

        closeDeleteMeetingDialog() {
            this.delete_meeting_dialog = false;
            this.meeting_to_delete = null;
        },

        async deleteMeeting() {
            if (!this.meeting_to_delete) return;

            this.deleting = true;
            try {
                const response = await this.$axios.delete(`api/meetings/delete_meeting/${this.meeting_to_delete.id}`);

                if (response.data.response) {
                    this.$toast.success(response.data.message);
                    this.closeDeleteMeetingDialog();
                    await this.fetchMeetings();
                } else {
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error deleting meeting:', error);
                this.$toast.error('Failed to delete meeting');
            } finally {
                this.deleting = false;
            }
        },

        // Helper methods for UI
        getMeetingStatusName(statusId) {
            const status = this.get_meeting_statuses?.find(s => s.id === statusId);
            return status?.name || 'Unknown';
        },

        getMeetingStatusColor(statusId) {
            const status = this.get_meeting_statuses?.find(s => s.id === statusId);
            return status?.color || 'grey';
        },

        getMeetingStatusTextColor(statusId) {
            const status = this.get_meeting_statuses?.find(s => s.id === statusId);
            // Use white text for darker colors, black for lighter colors
            const darkColors = ['primary', 'error', 'success', 'info'];
            return darkColors.includes(status?.color) ? 'white' : 'black';
        },

        getAgendaPreview(agenda) {
            if (!agenda) return '';
            // Strip HTML tags and limit to 100 characters
            const stripped = agenda.replace(/<[^>]*>/g, '');
            return stripped.length > 100 ? stripped.substring(0, 100) + '...' : stripped;
        },

        getAvatarColor(name) {
            const colors = [
                'primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info',
                'purple', 'pink', 'indigo', 'cyan', 'teal', 'green', 'orange', 'brown'
            ];
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },

        getInitials(name) {
            if (!name) return 'U';
            return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        },

        async refreshData() {
            this.refreshing = true;
            try {
                await Promise.all([
                    this.refreshMeetings(),
                    this.refreshUsers(),
                    this.refreshCompanies(),
                ]);
                this.$toast.success('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.$toast.error('Failed to refresh data');
            } finally {
                this.refreshing = false;
            }
        },

        // New helper methods for the updated UI
        getMeetingFrequencyColor(frequency) {
            switch (frequency) {
                case 'once':
                    return 'primary';
                case 'recurring':
                    return 'success';
                default:
                    return 'grey';
            }
        },

        formatMeetingDate(dateString) {
            if (!dateString) return '';
            return this.$date(dateString).format("MMM DD, YYYY");
        },

        formatMeetingTime(dateString) {
            if (!dateString) return '';
            return this.$date(dateString).format(this.$tz.time);
        },

        getCreatorName(meeting) {
            // Get the creator name from the meeting's user relation or from access list
            if (meeting.creator && meeting.creator.name) {
                return meeting.creator.name;
            }

            // Fallback: try to get the creator from access list (first entry is usually creator)
            if (meeting.access && meeting.access.length > 0) {
                const creator = meeting.access.find(access => access.user_id === meeting.user_id);
                if (creator && creator.user) {
                    return creator.user.name;
                }
                // If no specific creator found, use first access user
                if (meeting.access[0] && meeting.access[0].user) {
                    return meeting.access[0].user.name;
                }
            }

            return 'Unknown User';
        },

        // ── Google Calendar Methods ────────────────────────────────────────────

        async handleCalendarOAuthReturn() {
            const params = new URLSearchParams(window.location.search);
            const status = params.get('calendar');
            if (status === 'connected') {
                this.$toast.success('Google Calendar connected successfully!');
                this.calendarConnected = true;
                this.calendarJustConnectedAt = Date.now();
                localStorage.setItem('calendar_connected', 'true');
                // Clean up the URL
                const url = new URL(window.location.href);
                url.searchParams.delete('calendar');
                window.history.replaceState({}, '', url.toString());
                await this.verifyCalendarConnectionAfterOAuth();
                return true;
            } else if (status === 'error') {
                this.$toast.error('Failed to connect Google Calendar. Please try again.');
                const url = new URL(window.location.href);
                url.searchParams.delete('calendar');
                window.history.replaceState({}, '', url.toString());
                return true;
            }
            return false;
        },

        async verifyCalendarConnectionAfterOAuth() {
            const attempts = 8;
            for (let index = 0; index < attempts; index++) {
                const isConnected = await this.checkCalendarStatus({ suppressAuthExpiredToast: true, noCache: true });
                if (isConnected) {
                    return true;
                }
                if (index < attempts - 1) {
                    await new Promise(resolve => setTimeout(resolve, 1500));
                }
            }

            // Verification timed out — keep localStorage flag and connected state
            // so the button stays green. The user can open the dialog to see status.
            // Do NOT show a warning toast here; the toast already fires from loadCalendarUser
            // inside openCalendarDialog if the user manually opens it.
            return false;
        },

        handleCalendarTokenExpired() {
            if (this.calendarExpiredHandled) return;
            this.calendarExpiredHandled = true;
            this.calendarConnected = false;
            this.calendarUser = null;
            this.calendar_dialog = false;
            localStorage.removeItem('calendar_connected');
            this.$toast.warning('Google Calendar connection has expired. Please reconnect.');
            // Reset flag after a short delay so future page interactions can re-trigger if needed
            setTimeout(() => { this.calendarExpiredHandled = false; }, 3000);
        },

        isCalendarAuthError(message, code = null) {
            if (code === 'not_authenticated') return true;
            return typeof message === 'string' && (
                message.includes('not_authenticated') ||
                message.includes('invalid_credentials') ||
                message.includes('token') && message.includes('expired')
            );
        },

        isCalendarConnectedPayload(payload) {
            if (!payload || typeof payload !== 'object') return false;

            // Explicitly NOT connected
            if (payload.connected === false || payload.calendar_connected === false || payload.google_calendar_connected === false) {
                return false;
            }

            // ── Recall.ai specific fields ──────────────────────────────────────
            // Recall.ai returns google_calendar_authenticated (bool) and
            // google_calendar_authentication_status ('ok' | 'not_authenticated')
            if (payload.google_calendar_authenticated === true) return true;
            if (payload.google_calendar_authentication_status === 'ok') return true;
            if (payload.google_calendar_authenticated === false ||
                payload.google_calendar_authentication_status === 'not_authenticated') {
                return false;
            }

            // ── Generic / normalized fields ────────────────────────────────────
            if (payload.google === true) return true;
            if (payload.connected === true) return true;
            if (payload.calendar_connected === true) return true;
            if (payload.google_calendar_connected === true) return true;

            const provider = payload.provider || payload.providers?.google || payload.connection || payload.google;
            if (provider && typeof provider === 'object') {
                if (provider.connected === true) return true;
                if (provider.status === 'connected') return true;
            }

            if (typeof payload.google === 'object') {
                if (payload.google.connected === true) return true;
                if (payload.google.status === 'connected') return true;
            }

            // Fallback: backend returned profile-like payload for connected account.
            // Recall.ai uses 'uuid' and 'platform_email', so check those too.
            return !!(payload.id || payload.uuid || payload.email || payload.platform_email || payload.name);
        },

        async recoverCalendarAuthState() {
            if (this.calendarAuthRecoveryInProgress) {
                return this.calendarConnected;
            }

            this.calendarAuthRecoveryInProgress = true;
            try {
                const firstCheck = await this.checkCalendarStatus({ suppressAuthExpiredToast: true, noCache: true });
                if (firstCheck) return true;

                await new Promise(resolve => setTimeout(resolve, 1200));
                const secondCheck = await this.checkCalendarStatus({ suppressAuthExpiredToast: true, noCache: true });
                if (secondCheck) return true;
                return false;
            } finally {
                this.calendarAuthRecoveryInProgress = false;
            }
        },

        async checkCalendarStatus(options = {}) {
            const suppressAuthExpiredToast = !!options.suppressAuthExpiredToast;
            const noCache = !!options.noCache;
            try {
                const params = noCache ? { _t: Date.now() } : {};
                // Try Recall.ai first — only treat as connected if Recall.ai explicitly confirms it
                const res = await this.$axios.get('api/calendar/user', params);
                if (res.data.response && res.data.data) {
                    const isConnected = this.isCalendarConnectedPayload(res.data.data);
                    if (isConnected) {
                        this.calendarSource = 'recall';
                        this.calendarUser = res.data.data;
                        this.calendarConnected = true;
                        localStorage.setItem('calendar_connected', 'true');
                        this.autoRecordAll = res.data.data.recording_preferences?.all_meetings ?? false;
                        return true;
                    }
                    // Recall.ai returned data but user is NOT connected via Recall.ai
                    // Fall through to check direct Google Events connection below
                }

                // Try direct Google Events (OAuth-based) integration
                const eventsRes = await this.$axios.get('api/calendar/google-events-status', { _t: Date.now() });
                if (eventsRes.data.response && eventsRes.data.data?.connected) {
                    this.calendarSource = 'google_events';
                    this.calendarConnected = true;
                    localStorage.setItem('calendar_connected', 'true');
                    return true;
                }

                // Neither integration is connected
                if (!suppressAuthExpiredToast) {
                    const wasConnected = this.calendarConnected || localStorage.getItem('calendar_connected') === 'true';
                    if (wasConnected) this.handleCalendarTokenExpired();
                }
                this.calendarConnected = false;
                this.calendarSource = null;
                localStorage.removeItem('calendar_connected');
                return false;
            } catch (e) {
                console.warn('Calendar status check failed:', e);
            }
            return false;
        },

        async connectGoogleCalendar() {
            this.calendarConnecting = true;
            try {
                // Use the direct Google OAuth flow (google-events-oauth-url) which
                // stores the token in user_google_tokens table. Status is then
                // verified via google-events-status, keeping both flows consistent.
                const res = await this.$axios.get('api/calendar/google-events-oauth-url');
                if (res.data.response) {
                    window.location.href = res.data.data.url;
                } else {
                    this.$toast.error(res.data.message || 'Failed to get OAuth URL');
                }
            } catch (e) {
                console.error('Calendar connect error:', e);
                this.$toast.error('Failed to initiate Google Calendar connection');
            } finally {
                this.calendarConnecting = false;
            }
        },

        async openCalendarDialog() {
            this.calendar_dialog = true;
            this.calendarUser = null;
            this.calendarLoadError = false;
            this.calendarMeetings = [];
            await this.loadCalendarUser();
            // Only load Recall.ai meetings when that integration is active
            if (this.calendarSource === 'recall') {
                this.loadCalendarMeetings();
            }
        },

        async loadCalendarUser() {
            try {
                // Try Recall.ai calendar integration first
                const recallRes = await this.$axios.get('api/calendar/user', { _t: Date.now() });
                if (recallRes.data.response && recallRes.data.data) {
                    const isConnected = this.isCalendarConnectedPayload(recallRes.data.data);
                    if (isConnected) {
                        this.calendarSource = 'recall';
                        this.calendarUser = recallRes.data.data;
                        this.calendarConnected = true;
                        this.calendarLoadError = false;
                        localStorage.setItem('calendar_connected', 'true');
                        this.autoRecordAll = recallRes.data.data.recording_preferences?.all_meetings ?? false;
                        return;
                    }
                    // Recall.ai returned data but user is NOT connected via Recall.ai
                    // Fall through to check direct Google Events connection below
                }

                // Try direct Google Events (OAuth-based) integration
                const eventsRes = await this.$axios.get('api/calendar/google-events-status', { _t: Date.now() });
                if (eventsRes.data.response && eventsRes.data.data?.connected) {
                    this.calendarSource = 'google_events';
                    this.calendarConnected = true;
                    this.calendarUser = { email: this.get_user?.email || '', _source: 'google_events' };
                    this.calendarLoadError = false;
                    localStorage.setItem('calendar_connected', 'true');
                    return;
                }

                // Neither is connected
                this.calendarLoadError = true;
                this.calendarConnected = false;
                this.calendarSource = null;
                localStorage.removeItem('calendar_connected');
            } catch (e) {
                console.error('Error loading calendar user:', e);
                this.calendarLoadError = true;
            }
        },

        async loadCalendarMeetings() {
            this.calendarMeetingsLoading = true;
            try {
                const now = new Date();
                const twoWeeksLater = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
                const res = await this.$axios.get('api/calendar/meetings', {
                    start_time_gte: now.toISOString(),
                    start_time_lte: twoWeeksLater.toISOString(),
                });
                if (res.data.response) {
                    this.calendarMeetings = res.data.data?.results ?? res.data.data ?? [];
                } else {
                    this.calendarMeetings = [];
                }
            } catch (e) {
                console.error('Error loading calendar meetings:', e);
            } finally {
                this.calendarMeetingsLoading = false;
            }
        },

        async refreshCalendarMeetings() {
            this.calendarRefreshing = true;
            try {
                await this.$axios.post('api/calendar/meetings/refresh');
                await this.loadCalendarMeetings();
                this.$toast.success('Calendar meetings refreshed');
            } catch (e) {
                this.$toast.error('Failed to refresh calendar meetings');
            } finally {
                this.calendarRefreshing = false;
            }
        },

        async saveRecordingPreferences() {
            this.savingPreferences = true;
            try {
                await this.$axios.put('api/calendar/user', {
                    recording_preferences: { all_meetings: this.autoRecordAll }
                });
                this.$toast.success(this.autoRecordAll ? 'Auto-record enabled for all meetings' : 'Auto-record disabled');
            } catch (e) {
                this.$toast.error('Failed to save preferences');
                this.autoRecordAll = !this.autoRecordAll; // revert on error
            } finally {
                this.savingPreferences = false;
            }
        },

        async toggleMeetingRecord(meeting, value) {
            this.$set(meeting, '_saving', true);
            try {
                await this.$axios.put(`api/calendar/meetings/${meeting.id}`, {
                    override_should_record: value ? true : null
                });
                meeting.override_should_record = value ? true : null;
            } catch (e) {
                this.$toast.error('Failed to update meeting recording preference');
            } finally {
                this.$set(meeting, '_saving', false);
            }
        },

        async disconnectGoogleCalendar() {
            this.calendarDisconnecting = true;
            try {
                if (this.calendarSource === 'google_events') {
                    await this.$axios.post('api/calendar/google-events-disconnect');
                } else {
                    await this.$axios.post('api/calendar/disconnect', { platform: 'google' });
                }
                this.calendarConnected = false;
                this.calendarUser = null;
                this.calendarMeetings = [];
                this.calendarSource = null;
                this.calendar_dialog = false;
                localStorage.removeItem('calendar_connected');
                this.$toast.success('Google Calendar disconnected');
            } catch (e) {
                this.$toast.error('Failed to disconnect calendar');
            } finally {
                this.calendarDisconnecting = false;
            }
        },

        formatCalendarDate(dateString) {
            if (!dateString) return 'No date';
            return this.$date(dateString).format(`MMM DD, YYYY ${this.$tz.time}`);
        },

        // ─────────────────────────────────────────────────────────────────────────

        formatRecurrenceDays(days) {
            if (!days || !Array.isArray(days) || days.length === 0) return 'No days selected';
            
            const dayNames = {
                0: 'Sun',
                1: 'Mon', 
                2: 'Tue',
                3: 'Wed',
                4: 'Thu',
                5: 'Fri',
                6: 'Sat'
            };
            
            const sortedDays = [...days].sort((a, b) => a - b);
            const dayLabels = sortedDays.map(day => dayNames[day] || 'Unknown');
            
            if (dayLabels.length <= 2) {
                return dayLabels.join(' and ');
            } else if (dayLabels.length === 7) {
                return 'Every day';
            } else {
                return dayLabels.slice(0, -1).join(', ') + ' and ' + dayLabels[dayLabels.length - 1];
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.meetings-list {
    .meeting-item {
        border-radius: 12px;
        transition: all 0.2s ease;
        cursor: pointer;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        .meeting-info {
            .text-h6 {
                color: #1a1a1a;
                line-height: 1.2;
            }
        }

        .creator-info {
            min-width: 120px;
        }
    }
}

.empty-state {
    padding: 60px 20px;
}

.meeting-section {
    .section-header {
        padding: 12px 0;
        border-bottom: 2px solid #e0e0e0;

        .text-h6 {
            color: #1a1a1a;
        }
    }

    .meetings-scroll-container {
        max-height: 400px;
        overflow-y: auto;
        padding-right: 4px;

        // Custom scrollbar styling
        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 3px;

            &:hover {
                background: #a8a8a8;
            }
        }
    }
}

// Dialog improvements
.v-dialog {
    .v-card {
        border-radius: 12px;

        .v-card-title {
            padding-bottom: 8px;
            border-bottom: 1px solid #e0e0e0;
        }

        .v-card-text {
            padding-top: 20px;
        }
    }
}

.calendar-meeting-item {
    border-bottom: 1px solid #f0f0f0;
    min-height: 52px;

    &:last-child {
        border-bottom: none;
    }
}

// Form styling
.v-form {

    .v-text-field,
    .v-select {
        .v-input__control {
            .v-input__slot {
                border-radius: 8px;
            }
        }
    }
}
</style>
